<template>
  <div class="nt-page">
    <div class="container py-4">
      <div class="nt-head">
        <SectionHeader label="الإشعارات" title="مركز الإشعارات" subtitle="" />
        <button v-if="unread > 0" class="nt-markall" @click="markAll">
          <i class="pi pi-check-circle"></i> تعليم الكل كمقروء
        </button>
      </div>

      <div v-if="loading" class="nt-list">
        <div v-for="n in 5" :key="n" class="skel-row"></div>
      </div>

      <div v-else-if="error" class="nt-empty" role="alert"><p>{{ error }}</p><button class="nt-markall" @click="load">إعادة المحاولة</button></div>
      <div v-else-if="!items.length" class="nt-empty">
        <i class="pi pi-bell"></i>
        <p>لا توجد إشعارات بعد.</p>
      </div>

      <div v-else class="nt-list">
        <button v-for="n in items" :key="n.id" class="nt-item" :class="{ 'nt-item--unread': !n.read_at }" :disabled="opening === n.id" @click="open(n)">
          <span class="nt-item__icon" :class="toneClass(n)"><i class="pi" :class="icon(n)"></i></span>
          <span class="nt-item__body">
            <span class="nt-item__title">{{ n.data?.title || 'إشعار' }}</span>

            <!-- New content: structured content details -->
            <template v-if="n.data?.type === 'new_content'">
              <span v-if="n.data?.content_title" class="nt-item__content-name">{{ n.data.content_title }}</span>
              <span class="nt-item__meta">
                <span v-if="n.data?.content_type" class="nt-badge">🏷️ النوع: {{ n.data.content_type }}</span>
                <span v-if="n.data?.branch_name" class="nt-meta-line">🏫 الفرع: {{ n.data.branch_name }}</span>
                <span v-if="n.data?.unit_name" class="nt-meta-line">📘 الوحدة: {{ n.data.unit_name }}</span>
                <span v-if="n.data?.lesson_name" class="nt-meta-line">📖 الدرس: {{ n.data.lesson_name }}</span>
                <span v-if="n.data?.academic_year_name" class="nt-meta-line">🎓 الصف: {{ n.data.academic_year_name }}</span>
              </span>
            </template>

            <!-- Other notifications: unchanged -->
            <span v-else class="nt-item__msg">{{ n.data?.message }}</span>

            <span class="nt-item__time">{{ date(n.created_at) }}</span>
          </span>
          <span v-if="!n.read_at" class="nt-item__dot"></span>
        </button>
      </div>
      <button v-if="hasMore && !loading" class="nt-markall mt-3" :disabled="loadingMore" @click="loadMore">إشعارات أقدم</button>
    </div>
  </div>
</template>

<script>
import SectionHeader from '@/components/common/SectionHeader.vue';
import { notificationService, notificationDestination, updateNotificationBadge } from '@/services/notificationService.js';
const formatDate = value => new Intl.DateTimeFormat('ar-EG', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
const notificationIcon = type => ({ new_content: 'pi-book', new_lesson: 'pi-book', student_targeted: 'pi-bell', payment_approved: 'pi-check-circle', payment_rejected: 'pi-times-circle' }[type] || 'pi-bell');

const TONE = {
  payment_approved: 'green', subscription_activated: 'green',
  payment_rejected: 'red', subscription_expired: 'red',
  subscription_expiring: 'amber', payment_received: 'blue',
  new_content: 'purple', new_lesson: 'purple',
};

export default {
  name: 'NotificationsCenter',
  components: { SectionHeader },
  data() { return { items: [], unread: 0, loading: true, error: '', page: 1, hasMore: false, opening: null, loadingMore: false }; },
  methods: {
    date: formatDate,
    icon(n) { return notificationIcon(n.data?.type); },
    toneClass(n) { return 'nt-icon--' + (TONE[n.data?.type] || 'blue'); },
    async load() {
      this.loading = true;
      this.error = '';
      this.page = 1;
      try {
        const res = await notificationService.notifications();
        this.items = res.data || [];
        this.unread = res.unread_count || 0;
        this.hasMore = !!res.has_more;
        updateNotificationBadge(this.unread);
      } catch { this.error = 'تعذر تحميل الإشعارات. حاول مرة أخرى.'; }
      finally { this.loading = false; }
    },
    async loadMore() {
      if (this.loadingMore) return;
      this.loadingMore = true;
      try {
        const res = await notificationService.notifications(this.page + 1);
        this.items = [...new Map([...this.items, ...(res.data || [])].map(item => [item.id, item])).values()];
        this.page++;
        this.hasMore = !!res.has_more;
      } catch { this.error = 'تعذر تحميل الإشعارات. حاول مرة أخرى.'; }
      finally { this.loadingMore = false; }
    },
    async open(n) {
      if (this.opening) return;
      this.opening = n.id;
      // Deep-link to the most relevant page, and mark this one read.
      if (!n.read_at) {
        try { await notificationService.markNotificationRead(n.id); n.read_at = new Date().toISOString(); this.unread = Math.max(0, this.unread - 1); }
        catch { /* non-fatal */ }
      }
      updateNotificationBadge(this.unread);
      this.opening = null;
      const destination = notificationDestination(n.data);
      if (destination) { this.$router.push(destination); return; }
      const t = n.data?.type;
      if (t === 'new_content' || t === 'new_lesson' || t === 'student_targeted') {
        return;
      } else if (t === 'payment_received' || t === 'payment_approved' || t === 'payment_rejected') {
        this.$router.push('/payment-history');
      } else {
        this.$router.push('/my-subscriptions');
      }
    },
    async markAll() {
      try {
        await notificationService.markNotificationsRead();
        this.items = this.items.map(n => ({ ...n, read_at: n.read_at || new Date().toISOString() }));
        this.unread = 0;
        updateNotificationBadge(0);
      } catch { /* non-fatal */ }
    },
  },
  mounted() { this.load(); },
};
</script>

<style scoped>
.nt-page { min-height: 60vh; padding-bottom: 5rem; }
.nt-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: .5rem; }
.nt-markall { background: rgba(5, 22, 41,.08); color: #051629; border: none; border-radius: 10px; padding: .5rem .9rem; font-weight: 700; cursor: pointer; white-space: nowrap; }
.nt-list { display: grid; gap: .6rem; }
.nt-item { display: flex; align-items: center; gap: .8rem; background: #fff; border: 1px solid rgba(5, 22, 41,.08); border-radius: 14px; padding: .9rem 1rem; text-align: start; cursor: pointer; position: relative; width: 100%; }
.nt-item--unread { background: rgba(5, 22, 41,.035); border-color: rgba(5, 22, 41,.18); }
.nt-item__icon { width: 42px; height: 42px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; flex-shrink: 0; }
.nt-icon--green { background: rgba(34,197,94,.12); color: #16a34a; }
.nt-icon--red { background: rgba(239,68,68,.12); color: #dc2626; }
.nt-icon--amber { background: rgba(245,158,11,.14); color: #b45309; }
.nt-icon--blue { background: rgba(5, 22, 41,.1); color: #051629; }
.nt-icon--purple { background: rgba(124,77,255,.12); color: #7C4DFF; }
.nt-item__body { display: flex; flex-direction: column; gap: .15rem; flex: 1; }
.nt-item__title { font-weight: 800; color: #111827; font-size: .92rem; }
.nt-item__msg { color: #6B7280; font-size: .82rem; line-height: 1.5; }
.nt-item__content-name { font-weight: 800; color: #4338ca; font-size: .9rem; line-height: 1.4; }
.nt-item__meta { display: flex; flex-direction: column; gap: .2rem; margin-top: .15rem; }
.nt-badge { align-self: flex-start; display: inline-flex; align-items: center; gap: .25rem; background: rgba(124,77,255,.1); color: #6d28d9; font-size: .75rem; font-weight: 700; padding: .12rem .55rem; border-radius: 999px; margin-bottom: .1rem; }
.nt-meta-line { color: #6B7280; font-size: .8rem; line-height: 1.55; }
.nt-item__time { color: #94a3b8; font-size: .72rem; }
.nt-item__dot { width: 9px; height: 9px; border-radius: 50%; background: #051629; flex-shrink: 0; }
.nt-empty { text-align: center; padding: 3.5rem 1rem; color: #94a3b8; }
.nt-empty .pi { font-size: 2.6rem; display: block; margin-bottom: .8rem; }
.skel-row { height: 68px; border-radius: 14px; background: #e9edf5; position: relative; overflow: hidden; }
.skel-row::after { content: ''; position: absolute; inset: 0; transform: translateX(-100%); background: linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent); animation: sh 1.3s infinite; }
@keyframes sh { 100% { transform: translateX(100%); } }
</style>
